import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.API_BASE_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: config.API_TIMEOUT,
});

// Add authentication token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Menu APIs
export const menuAPI = {
  getAll: () => api.get('/menu'),
  getById: (id) => api.get(`/menu/${id}`),
  create: (data) => api.post('/menu', data),
  update: (id, data) => api.put(`/menu/${id}`, data),
  delete: (id) => api.delete(`/menu/${id}`),
  getByCategory: (category) => api.get(`/menu/category/${category}`),
};

// Order APIs
export const orderAPI = {
  getAll: (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get('/orders', { params });
  },
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  updateOrder: (id, data) => api.put(`/orders/${id}`, data),
  getByTable: (tableNumber) => api.get(`/orders/table/${tableNumber}`),
  getByStatus: (status) => api.get(`/orders/status/${status}`),
};

// Inventory APIs
export const inventoryAPI = {
  getAll: () => api.get('/inventory'),
  getStock: (id) => api.get(`/inventory/${id}/stock`),
  restock: (id, quantity, notes) => api.post(`/inventory/${id}/restock`, { quantity, notes }),
  getLowStock: () => api.get('/inventory/low-stock'),
  getOutOfStock: () => api.get('/inventory/out-of-stock'),
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_BASE_URL}/inventory/bulk-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplate: () => {
    return fetch(`${API_BASE_URL}/inventory/csv-template`)
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      });
  }
};

// Printing APIs
export const printingAPI = {
  getBill: (orderId) => api.get(`/printing/bill/${orderId}`, { responseType: 'blob' }),
  printBill: (orderId) => api.post(`/printing/bill/${orderId}/print`, {}),
};

// Reports APIs
export const reportsAPI = {
  getDaily: (date) => api.get('/reports/daily', { params: { date } }),
  getRange: (startDate, endDate) => api.get('/reports/range', { params: { start_date: startDate, end_date: endDate } }),
  getSummary: () => api.get('/reports/summary'),
};

// Daily Inventory APIs
export const dailyInventoryAPI = {
  openDay: (businessDate, openedBy) => api.post('/daily-inventory/open-day', { business_date: businessDate, opened_by: openedBy }),
  closeDay: (businessDate, closedBy, notes, wastage) => api.post('/daily-inventory/close-day', { business_date: businessDate, closed_by: closedBy, notes, wastage }),
  getToday: () => api.get('/daily-inventory/today'),
  getHistory: (startDate, endDate, limit) => api.get('/daily-inventory/history', { params: { start_date: startDate, end_date: endDate, limit } }),
  getByDate: (date) => api.get(`/daily-inventory/date/${date}`),
  getUnclosed: () => api.get('/daily-inventory/unclosed'),
  getStatus: () => api.get('/daily-inventory/status'),
  addWastage: (businessDate, wastageData) => api.post('/daily-inventory/add-wastage', { business_date: businessDate, ...wastageData }),
};
